import { pluralize } from '@common/services';
import {
  formatOverrideRateOptionLabel,
  formatTierOptionLabel,
} from '@scheduling/services';
import type {
  ApplyOverrideInputType,
  BulkOverridePreviewRowType,
  SelectedGapEntryType,
  Step2SelectionsType,
  WorkforceGroupOverrideSelectionType,
} from './_bulk-shift-step-types';

// Stable identity for an option in a Select. Mirrors the GraphQL identity
// tuple `(incentiveCents, moneyAmountCents)` per `OverrideRateOption` so we
// can persist the pre-fill / current pick without ambiguity in money mode
// (where two options can share `incentiveCents` and differ only on lump sum).
export function buildOptionValueKey(
  incentiveCents: number,
  moneyAmountCents: number | null | undefined
): string {
  return `${incentiveCents}::${moneyAmountCents ?? 'null'}`;
}

// Build the dropdown option label for either Select branch.
//
// RESOLVER_OPTIONS: each option's `moneyAmountCents` is gap-independent
// (server-supplied per option in money-PCE mode), so the lump-sum label is
// safe whenever every selected gap × WFG opted into `displayAsLumpSum` on its
// rate card. Falls back to `$X.XX/hr` otherwise.
//
// STATIC_TIERS: tiers don't carry a per-option `moneyAmountCents` and bulk
// selections can span gaps with different durations, so we can't render a
// single lump-sum total here without picking a duration. Stay hourly.
export function buildOptionLabel({
  controlMode,
  option,
  displayAsLumpSumUniform,
}: {
  controlMode: BulkOverridePreviewRowType['controlMode'];
  option: BulkOverridePreviewRowType['intersectedOptions'][number];
  displayAsLumpSumUniform: boolean;
}): string {
  if (controlMode === 'STATIC_TIERS') {
    return formatTierOptionLabel(
      { label: option.displayName, payRate: option.incentiveCents / 100 },
      false,
      0
    );
  }

  return formatOverrideRateOptionLabel(option, displayAsLumpSumUniform, 0);
}

// `<Select>` value (string) for a chosen option. Maps the persisted
// `(incentiveCents, moneyAmountCents)` pair the FE keeps to the unique key
// the dropdown options are rendered with.
export function selectionToValueKey(
  selection: WorkforceGroupOverrideSelectionType | undefined
): string | undefined {
  if (!selection) return undefined;
  return buildOptionValueKey(
    selection.incentiveCents,
    selection.moneyAmountCents
  );
}

// Reverse mapping: turn the value key emitted by `<Select onChange>` back
// into the option object so we can stash both `incentiveCents` and
// `moneyAmountCents` in wizard state without re-traversing the option list
// on each render.
export function findOptionByValueKey(
  options: BulkOverridePreviewRowType['intersectedOptions'],
  valueKey: string
): BulkOverridePreviewRowType['intersectedOptions'][number] | undefined {
  return options.find(
    (option) =>
      buildOptionValueKey(option.incentiveCents, option.moneyAmountCents) ===
      valueKey
  );
}

// Default selection seeded from the server's `uniformDefault`. Always null on
// FREE_FORM (the input opens empty by design) and on DISABLED (no choice
// possible), per Step 2 plan.
export function buildSelectFromUniformDefault(
  row: BulkOverridePreviewRowType
): WorkforceGroupOverrideSelectionType | null {
  if (
    row.controlMode !== 'RESOLVER_OPTIONS' &&
    row.controlMode !== 'STATIC_TIERS'
  ) {
    return null;
  }
  if (!row.uniformDefault) return null;

  return {
    workforceGroupId: row.workforceGroup.id,
    workforceGroupName: row.workforceGroup.name,
    incentiveCents: row.uniformDefault.incentiveCents,
    moneyAmountCents: row.uniformDefault.moneyAmountCents ?? null,
    controlMode: row.controlMode,
    displayAsLumpSumUniform: row.displayAsLumpSumUniform,
  };
}

// Disabled-row helper copy. The plan groups all disabled cases under one
// `DISABLED` state on the FE; the actual reason (`disabledReason`) selects
// the message. Counts come straight from the preview row so we don't have
// to re-derive them.
export function buildDisabledHelperCopy(
  row: BulkOverridePreviewRowType
): string {
  if (row.disabledReason === 'NO_COMMON_OPTION') {
    return "The selected shifts don't share a rate option for this group. Try selecting shifts from the same unit or with the same shift length.";
  }

  if (row.disabledReason === 'SOME_GAPS_READ_ONLY') {
    const totalGapCount = row.editableGapCount + row.nonEditableGapCount;
    return `${row.nonEditableGapCount} of ${totalGapCount} selected shifts are read-only for this group (configure pay codes first).`;
  }

  if (row.disabledReason === 'ALL_GAPS_READ_ONLY') {
    return "Rate overrides aren't available for this group — pay codes aren't configured for the selected shifts.";
  }

  return '';
}

// FREE_FORM helper copy ("Current rates: $X.XX–$Y.YY/hr") sourced from the
// server's `effectiveRateRangeCents`. Returns null when the range is missing
// (server omits it on non-free-form rows) or unusable, so callers can skip
// the line entirely instead of rendering "Current rates: $0.00–$0.00/hr".
export function buildFreeFormRateRangeHint(
  row: BulkOverridePreviewRowType
): string | null {
  const range = row.effectiveRateRangeCents;
  if (!range) return null;
  if (range.min === 0 && range.max === 0) return null;

  const min = (range.min / 100).toFixed(2);
  const max = (range.max / 100).toFixed(2);
  if (range.min === range.max) return `Current rate: $${min}/hr.`;

  return `Current rates: $${min}–$${max}/hr.`;
}

// Step 2's `Continue` gate. Plan: at least one WFG must have a non-empty
// selection; disabled rows can never count.
export function hasAnyValidSelection(selections: Step2SelectionsType): boolean {
  return Object.values(selections).some(isValidSelection);
}

export function isValidSelection(
  selection: WorkforceGroupOverrideSelectionType | undefined | null
): selection is WorkforceGroupOverrideSelectionType {
  if (!selection) return false;
  return (
    Number.isFinite(selection.incentiveCents) && selection.incentiveCents > 0
  );
}

// Apply-mutation payload. Skips disabled rows / unset rows so the server
// receives only WFGs the user explicitly chose (matches the "no implicit
// deletes" rule).
export function buildApplyOverridesPayload(
  selections: Step2SelectionsType
): ApplyOverrideInputType[] {
  return Object.values(selections)
    .filter(isValidSelection)
    .map((selection) => ({
      workforceGroupId: selection.workforceGroupId,
      incentiveCents: selection.incentiveCents,
      moneyAmountCents: selection.moneyAmountCents,
    }));
}

// Step 3 line-item label. The Confirm Rate Overrides screen uses one consistent
// shape across resolver / static / free-form: `<WFG>: $X.XX/hr · N shifts`.
// Tier labels (`displayName`) live on Step 2's dropdown — Step 3 condenses to
// the rate so each line stays scannable in the summary.
export function buildConfirmationLine({
  selection,
  shiftCount,
}: {
  selection: WorkforceGroupOverrideSelectionType;
  shiftCount: number;
}): string {
  if (selection.controlMode === 'FREE_FORM') {
    const dollars = (selection.incentiveCents / 100).toFixed(2);
    return `${selection.workforceGroupName}: $${dollars}/hr · ${shiftCount} ${pluralize('shift', shiftCount)}`;
  }

  const optionLabel = buildOptionLabel({
    controlMode: selection.controlMode,
    option: {
      displayName: selection.workforceGroupName,
      incentiveCents: selection.incentiveCents,
      moneyAmountCents: selection.moneyAmountCents,
    },
    displayAsLumpSumUniform: selection.displayAsLumpSumUniform,
  });

  return `${selection.workforceGroupName}: ${optionLabel} · ${shiftCount} ${pluralize('shift', shiftCount)}`;
}

// Friendly summary for the confirm screen header — handy for screen readers
// even when the dedup count is the same number rendered higher up the page.
export function describeShiftCount(
  selectedGaps: SelectedGapEntryType[]
): string {
  const count = selectedGaps.length;
  return `${count} ${pluralize('shift', count)}`;
}

// Format raw cents as currency for the `placeholder` / hint text. Kept here
// so the Step 2 component stays free of formatting concerns and the helper
// is unit-tested.
export function formatHourlyRateCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}/hr`;
}
